import type {SignalToWeb} from "@khanacademy/embedded-webapp-schema";

import {couldBeIntegration} from "./could-be-integration.ts";
import {SignalListener} from "./signal-listener.ts";
import {subscribeToIncomingMessages} from "./subscribe-to-incoming-messages.ts";

/**
 * Singleton instance of our message listener.
 */
let _messageListener: SignalListener | void;
/**
 * Accessor to ensure we only create an instance iff we need it.
 */
function listener() {
    _messageListener =
        _messageListener ?? new SignalListener(subscribeToIncomingMessages);
    return _messageListener;
}

/**
 * Constant no-op function for non-mobile app usage.
 */
const noop = () => {};

/**
 * Reset any persistent module state.
 *
 * Providing this so that we can tidy-up between tests.
 *
 * In non-test scenarios, things should be properly cleaning up, or there's
 * a full-page navigation and we don't care. For dev, we throw an error when
 * this is called to let folks know they shouldn't call it. In prod, it's a
 * no-op.
 */
export function reset() {
    if (process.env.NODE_ENV === "test") {
        if (_messageListener != null) {
            _messageListener.tearDown();
            _messageListener = undefined;
        }
    } else if (process.env.NODE_ENV !== "production") {
        throw new Error("reset() should only be called in tests");
    }
}

/**
 * Listen for a message from the mobile app.
 *
 * If we are certain the mobile app isn't present, this is a no-op, as is the
 * returned function. Otherwise, this ensures that the we are subscribed to
 * the window.onmessage event, and that the given callback is subscribed
 * to the specific message type with the given callback.
 *
 * You must provide the type parameter defining the signal being listened for.
 * This ensures that the arguments are properly type checked against that
 * signal to make sure they match (this is why we default `TSignal` to `never`).
 *
 * @param name The name of the message to listen for.
 * @param callback The callback to invoke when the message is received.
 * @returns A function that can be used to unsubscribe from the message.
 */
export function listenForSignal<TSignal extends SignalToWeb = never>(
    name: TSignal["type"],
    callback: (data: TSignal["data"]) => void,
): () => void {
    if (!couldBeIntegration()) {
        return noop;
    }
    return listener().listen(name, callback);
}
